import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled, useTheme } from '@mui/material/styles';
import {
  Box,
  Card,
  CardContent,
  Grid,
  MenuItem,
  TextField,
  Typography,
  Chip,
  IconButton,
  Avatar,
  Tooltip,
  Badge,
  Stack,
  InputAdornment,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Info,
  CheckCircle,
  RadioButtonUnchecked,
  Search,
  Notifications,
  MarkEmailRead,
  MarkEmailUnread,
  NotificationsOff,
  NotificationsActive
} from '@mui/icons-material';
import { Pagination, Snackbar, Alert } from '@mui/material';
import Title from '../../components/Title';
import {
  useGetNoticesQuery,
  useGetNoticeTypesQuery,
  useSetNoticeViewedMutation
} from '../../redux/api/noticesApi';
import { formatDate } from '../../resources/functions';
import { Notice } from '../../resources/types/noticesTypes';
import { DateRange } from '../../types/dateRange';
import { Moment } from 'moment';
import moment from 'moment';
import DateTimeRangePicker from '../../components/DateTimeRangePicker';
import {
  isNotificationsEnabled,
  setNotificationsEnabled
} from '../../utils/notificationsSettings';

const DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

const Root = styled('div')(({ theme }) => ({
  padding: '0',
  minHeight: 'calc(100vh - 180px)'
}));

const FilterBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: 12,
  marginBottom: 20,
  flexWrap: 'wrap',
  alignItems: 'center',
  padding: '16px',
  backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : '#2A2A2A',
  borderRadius: 12,
  border: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.1)'}`
}));

const NoticeCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  transition: 'all 0.2s ease',
  border: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.1)'}`,
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.palette.mode === 'light' 
      ? '0 8px 24px rgba(0, 0, 0, 0.12)' 
      : '0 8px 24px rgba(0, 0, 0, 0.4)'
  }
}));

const CountersContainer = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: 12,
  marginBottom: 20
});

const hexToRgba = (hex: string, alpha: number = 1): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

interface CounterCardProps {
  bgColor: string;
}

const CounterCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'bgColor'
})<CounterCardProps>(({ bgColor }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-start',
  padding: '14px 16px',
  borderRadius: '12px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  backgroundColor: hexToRgba(bgColor, 0.12),
  border: 'none',
  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
  minWidth: 160,
  minHeight: 80,
  position: 'relative',
  
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  }
}));

const IconCircle = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'bgColor'
})<CounterCardProps>(({ bgColor }) => ({
  width: 40,
  height: 40,
  borderRadius: '8px',
  backgroundColor: hexToRgba(bgColor, 0.18),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  '& svg': {
    width: 24,
    height: 24,
    color: bgColor
  }
}));

const CountText = styled(Box)<{ textColor: string }>(({ textColor }) => ({
  fontSize: 32,
  fontWeight: 700,
  color: textColor,
  lineHeight: 1,
  position: 'absolute',
  right: '16px',
  top: '50%',
  transform: 'translateY(-50%)'
}));

const LabelText = styled('span')(({ theme }) => ({
  fontSize: 11,
  fontWeight: 600,
  color: theme.palette.text.secondary,
  textTransform: 'uppercase',
  letterSpacing: '0.8px',
  textAlign: 'left'
}));

const limit = 20;

const getNoticeTypeConfig = (type: string) => {
  return {
    backgroundColor: 'rgba(33, 150, 243, 0.12)',
    iconColor: '#2196F3',
    icon: Info
  };
};

const renderTextWithBold = (text: string) => {
  const parts = text.split(/(<b>.*?<\/b>)/g);
  return parts.map((part, index) => {
    if (part.startsWith('<b>') && part.endsWith('</b>')) {
      const boldText = part.replace(/<\/?b>/g, '');
      return <strong key={index}>{boldText}</strong>;
    }
    return <span key={index}>{part}</span>;
  });
};

const NoticesPage2 = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [viewed, setViewed] = useState('');
  const [type, setType] = useState('');
  const [searchText, setSearchText] = useState('');
  const [dateRange, setDateRange] = useState<DateRange<Moment | null>>([
    null,
    null
  ]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info'>('success');
  const [notificationsEnabled, setNotificationsEnabledState] = useState(isNotificationsEnabled());

  const dateFilter = useMemo(() => {
    const isValid = dateRange?.[0]?.isValid() && dateRange?.[1]?.isValid();
    if (!isValid) return {};
    const dateFrom = dateRange?.[0]?.format?.(DATE_FORMAT);
    const dateTo = dateRange?.[1]?.format?.(DATE_FORMAT);
    return { dateFrom, dateTo };
  }, [dateRange]);

  const { data, isLoading, isError, error } = useGetNoticesQuery({
    limit,
    page,
    viewed,
    type,
    ...dateFilter,
    ...(searchText ? { scooterName: searchText } : {})
  });
  const notices = data?.adminNotices || [];

  // Calculate date 24 hours ago for counters
  const last24HoursFilter = useMemo(() => ({
    dateFrom: moment().subtract(24, 'hours').format(DATE_FORMAT)
  }), []);

  // Separate queries for counters (get total counts across all pages) - last 24 hours only
  const { data: totalNoticesData } = useGetNoticesQuery({
    limit: 1,
    page: 1,
    type,
    ...last24HoursFilter
  });

  const { data: newNoticesData } = useGetNoticesQuery({
    limit: 1,
    page: 1,
    viewed: 'false',
    type,
    ...last24HoursFilter
  });

  const { data: viewedNoticesData } = useGetNoticesQuery({
    limit: 1,
    page: 1,
    viewed: 'true',
    type,
    ...last24HoursFilter
  });

  // Debug logging
  console.log('📬 Notices API Response:', {
    data,
    isLoading,
    isError,
    error,
    noticesCount: notices.length,
    params: { limit, page, viewed, type, ...dateFilter }
  });

  const { data: noticeTypesData } = useGetNoticeTypesQuery();
  const noticeTypes = noticeTypesData || [];

  const [setNoticeViewed, { isLoading: updateNoticePending }] =
    useSetNoticeViewedMutation();

  const changePage = useCallback((e: any, nextPage: number) => {
    setPage(nextPage);
  }, []);

  const updateNoticeViewed = useCallback(
    (notice: Notice) => () => {
      setNoticeViewed({ id: notice._id, viewed: !notice.viewed });
    },
    [setNoticeViewed]
  );

  const stats = useMemo(() => {
    const total = totalNoticesData?.pagination.count || 0;
    const newCount = newNoticesData?.pagination.count || 0;
    const viewedCount = viewedNoticesData?.pagination.count || 0;
    return { total, viewedCount, newCount };
  }, [totalNoticesData?.pagination.count, newNoticesData?.pagination.count, viewedNoticesData?.pagination.count]);

  const handleNotificationsToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const enabled = event.target.checked;
    setNotificationsEnabled(enabled);
    setNotificationsEnabledState(enabled);
    setSnackbarMessage(enabled ? t('notices.mapNotificationsOn') : t('notices.mapNotificationsOff'));
    setSnackbarSeverity('info');
    setSnackbarOpen(true);
  };

  return (
    <Root>
      <Box sx={{ mb: 3 }}>
        <Title>{t('notices.title')}</Title>
      </Box>

      <Box sx={{ mb: 1.5 }}>
        <Typography 
          variant="body2" 
          sx={{ 
            color: theme.palette.mode === 'light' ? '#666' : '#999',
            fontSize: '0.875rem',
            fontWeight: 500
          }}
        >
          {t('notices.last24Hours')}
        </Typography>
      </Box>

      <CountersContainer>
        <CounterCard 
          bgColor="#2196F3"
          onClick={() => {
            setViewed('');
            setPage(1);
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <IconCircle bgColor="#2196F3">
              <Notifications />
            </IconCircle>
            <LabelText>{t('notices.total')}</LabelText>
          </Box>
          <CountText textColor="#2196F3">{stats.total}</CountText>
        </CounterCard>

        <CounterCard 
          bgColor="#FF9800"
          onClick={() => {
            setViewed('false');
            setPage(1);
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <IconCircle bgColor="#FF9800">
              <MarkEmailUnread />
            </IconCircle>
            <LabelText>{t('notices.new')}</LabelText>
          </Box>
          <CountText textColor="#FF9800">{stats.newCount}</CountText>
        </CounterCard>

        <CounterCard 
          bgColor="#4CAF50"
          onClick={() => {
            setViewed('true');
            setPage(1);
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <IconCircle bgColor="#4CAF50">
              <MarkEmailRead />
            </IconCircle>
            <LabelText>{t('notices.viewed')}</LabelText>
          </Box>
          <CountText textColor="#4CAF50">{stats.viewedCount}</CountText>
        </CounterCard>
      </CountersContainer>

      <FilterBar>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', flex: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={notificationsEnabled}
                onChange={handleNotificationsToggle}
                color="primary"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {notificationsEnabled ? (
                  <NotificationsActive sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                ) : (
                  <NotificationsOff sx={{ fontSize: 20, color: 'text.secondary' }} />
                )}
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {notificationsEnabled ? t('notices.mapNotificationsOn') : t('notices.mapNotificationsOff')}
                </Typography>
              </Box>
            }
            sx={{
              backgroundColor: notificationsEnabled
                ? theme.palette.mode === 'light'
                  ? 'rgba(33, 150, 243, 0.08)'
                  : 'rgba(33, 150, 243, 0.15)'
                : theme.palette.mode === 'light'
                  ? 'rgba(0, 0, 0, 0.04)'
                  : 'rgba(255, 255, 255, 0.04)',
              borderRadius: 2,
              px: 1.5,
              py: 0.5,
              mr: 1
            }}
          />

          <TextField
            placeholder={t('notices.searchPlaceholder')}
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setPage(1);
            }}
            size="small"
            sx={{ 
              minWidth: 280,
              '& .MuiOutlinedInput-root': {
                borderRadius: 3
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            select
            value={type}
            onChange={(e) => setType(e.target.value)}
            label={t('notices.type')}
            size="small"
            sx={{ 
              minWidth: 160,
              '& .MuiOutlinedInput-root': {
                borderRadius: 3
              }
            }}
          >
            <MenuItem value="">{t('notices.allTypes')}</MenuItem>
            {noticeTypes.map(noticeType => (
              <MenuItem key={noticeType} value={noticeType}>
                {noticeType}
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <DateTimeRangePicker
              startText={t('notices.from')}
              endText={t('notices.to')}
              value={dateRange}
              onChange={(date: DateRange<Moment>) => {
                setDateRange(date);
              }}
            />
          </Box>
        </Box>
      </FilterBar>

      <Grid container spacing={2}>
        {notices.map(notice => {
          const typeConfig = getNoticeTypeConfig(notice.type);
          const TypeIcon = typeConfig.icon;
          
          return (
            <Grid item xs={12} key={notice._id}>
              <NoticeCard>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Tooltip title={t('notices.noticeTooltip')} arrow>
                      <Avatar
                        sx={{
                          width: 48,
                          height: 48,
                          backgroundColor: typeConfig.backgroundColor,
                          borderRadius: '12px'
                        }}
                      >
                        <TypeIcon sx={{ fontSize: 28, color: typeConfig.iconColor }} />
                      </Avatar>
                    </Tooltip>

                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="caption" sx={{ 
                          color: 'text.secondary',
                          backgroundColor: theme.palette.mode === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)',
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1
                        }}>
                          {formatDate(notice.createdAt, { time: true })}
                        </Typography>
                        {!notice.viewed && (
                          <Chip 
                            label={t('notices.newBadge')} 
                            size="small" 
                            sx={{ 
                              backgroundColor: '#f5576c',
                              color: '#FFFFFF',
                              fontWeight: 600,
                              height: 24
                            }} 
                          />
                        )}
                      </Box>
                      <Typography variant="body1" sx={{ color: 'text.primary', lineHeight: 1.6 }}>
                        {renderTextWithBold(notice.text)}
                      </Typography>
                    </Box>

                    <Tooltip title={notice.viewed ? t('notices.markAsUnread') : t('notices.markAsRead')}>
                      <IconButton
                        onClick={updateNoticeViewed(notice)}
                        disabled={updateNoticePending}
                        sx={{
                          backgroundColor: notice.viewed 
                            ? (theme.palette.mode === 'light' ? '#E8F5E9' : 'rgba(76, 175, 80, 0.12)')
                            : (theme.palette.mode === 'light' ? '#F5F5F5' : 'rgba(255, 255, 255, 0.08)'),
                          color: notice.viewed ? '#4CAF50' : 'text.secondary',
                          '&:hover': {
                            backgroundColor: notice.viewed 
                              ? (theme.palette.mode === 'light' ? '#C8E6C9' : 'rgba(76, 175, 80, 0.2)')
                              : (theme.palette.mode === 'light' ? '#E0E0E0' : 'rgba(255, 255, 255, 0.12)')
                          }
                        }}
                      >
                        {notice.viewed ? <CheckCircle /> : <RadioButtonUnchecked />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardContent>
              </NoticeCard>
            </Grid>
          );
        })}
      </Grid>

      {notices.length === 0 && !isLoading && (
        <Box sx={{ 
          textAlign: 'center', 
          py: 8,
          backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2A2A2A',
          borderRadius: 3,
          mt: 2
        }}>
          <Info sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" sx={{ color: 'text.secondary' }}>
            {t('notices.notFound')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
            {t('notices.tryChangeFilters')}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Pagination 
          count={data?.pagination.totalPages || 0} 
          page={page}
          onChange={changePage}
          size="large"
          sx={{
            '& .MuiPaginationItem-root': {
              borderRadius: 2,
              fontWeight: 600,
              '&.Mui-selected': {
                backgroundColor: theme.palette.mode === 'light' ? '#000000' : '#FFFFFF',
                color: theme.palette.mode === 'light' ? '#FFFFFF' : '#000000',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'light' ? '#212121' : '#E0E0E0'
                }
              }
            }
          }}
        />
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          sx={{ 
            borderRadius: 2,
            backgroundColor: snackbarSeverity === 'success' 
              ? (theme.palette.mode === 'light' ? '#424242' : '#5073F8')
              : (theme.palette.mode === 'light' ? '#f44336' : '#ff5252'),
            color: theme.palette.mode === 'light' ? '#FFFFFF' : '#000000'
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Root>
  );
};

export default NoticesPage2;
